import React, { useState, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { IconSparkles, IconRefresh, IconInfoCircle, IconCheck, IconGenderFemale, IconGenderMale } from '@tabler/icons-react';
import BusinessTypeSelector from '../components/BusinessTypeSelector';
import GUIDED_QUESTIONS from '../constants/guidedQuestions';
import AgentsFlowService from '../../../services/AgentsFlowService';

const TOTAL_CHAR_BUDGET = 20000;

// Remove caracteres invisíveis que LLMs (ChatGPT, Claude, Gemini) costumam
// incluir e que inflam o contador sem agregar conteúdo: zero-width space (U+200B),
// zero-width non-joiner (U+200C), zero-width joiner (U+200D), word joiner (U+2060),
// BOM (U+FEFF), variation selectors (U+FE00-U+FE0F).
const INVISIBLE_CHARS_RE = /[​-‍⁠﻿︀-️]/g;

const sanitizePastedText = (text) => {
  if (!text) return '';
  return text
    .replace(INVISIBLE_CHARS_RE, '')
    .replace(/\r\n/g, '\n')   // normaliza line endings
    .replace(/\r/g, '\n');
};

// Handler genérico de paste: intercepta, sanitiza e injeta via setter.
// Necessário porque o paste padrão do textarea pode trazer chars invisíveis
// que driblam o contador.
const makePasteHandler = (currentValue, setter) => (e) => {
  const pasted = e.clipboardData?.getData('text');
  if (!pasted) return;
  e.preventDefault();
  const sanitized = sanitizePastedText(pasted);
  const target = e.target;
  const start = target.selectionStart ?? currentValue.length;
  const end = target.selectionEnd ?? currentValue.length;
  const next = currentValue.slice(0, start) + sanitized + currentValue.slice(end);
  setter(next);
  // Reposiciona o cursor após o texto colado, no próximo tick.
  requestAnimationFrame(() => {
    const pos = start + sanitized.length;
    try { target.setSelectionRange(pos, pos); } catch (_e) { /* noop */ }
  });
};

const IdealizationStep = ({ wizardState, setWizardState }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showRefinement, setShowRefinement] = useState(false);
  const [refinementNotes, setRefinementNotes] = useState('');
  const [error, setError] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [answers, setAnswers] = useState({});
  const [additionalInfo, setAdditionalInfo] = useState('');

  const { businessType, assistantGender, idealizationSummary } = wizardState;
  const iteration = wizardState._idealizationIteration || 0;

  // Questions for the selected business type
  const questions = useMemo(() => {
    return GUIDED_QUESTIONS[businessType] || [];
  }, [businessType]);

  // Progress calculation
  const answeredCount = useMemo(() => {
    return Object.entries(selectedQuestions)
      .filter(([id, selected]) => selected && answers[id]?.trim())
      .length;
  }, [selectedQuestions, answers]);

  const selectedCount = useMemo(() => {
    return Object.values(selectedQuestions).filter(Boolean).length;
  }, [selectedQuestions]);

  const progressPercent = useMemo(() => {
    if (selectedCount === 0) return 0;
    return Math.round((answeredCount / selectedCount) * 100);
  }, [answeredCount, selectedCount]);

  // Format answers into business_description string
  const formatDescription = () => {
    const parts = [];
    questions.forEach((q) => {
      if (selectedQuestions[q.id] && answers[q.id]?.trim()) {
        parts.push(`${q.question}\n${answers[q.id].trim()}`);
      }
    });
    if (additionalInfo.trim()) {
      parts.push(`Informações adicionais:\n${additionalInfo.trim()}`);
    }
    return parts.join('\n\n');
  };

  // Total de chars do payload real (inclui labels das perguntas, separadores
  // e additionalInfo). Recalcula em todo render para refletir o estado atual.
  const totalChars = useMemo(
    () => formatDescription().length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedQuestions, answers, additionalInfo, questions]
  );
  const isOverBudget = totalChars > TOTAL_CHAR_BUDGET;
  const canAnalyze = answeredCount >= 3 && !isOverBudget;

  const handleToggleQuestion = (questionId) => {
    setSelectedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
    // Clear idealization when answers change
    if (idealizationSummary) {
      setWizardState((prev) => ({
        ...prev,
        idealizationSummary: null,
        _idealizationApproved: false,
      }));
    }
  };

  const handleAnswerChange = (questionId, value) => {
    // Sanitiza tudo que entra no state — defesa em profundidade contra
    // drag-and-drop, autopreenchimento de extensão e outros caminhos que
    // não passam pelo onPaste.
    const clean = sanitizePastedText(value);
    setAnswers((prev) => ({
      ...prev,
      [questionId]: clean,
    }));
    if (idealizationSummary) {
      setWizardState((prev) => ({
        ...prev,
        idealizationSummary: null,
        _idealizationApproved: false,
      }));
    }
  };

  const handleAnalyze = async (isRefine = false) => {
    setIsAnalyzing(true);
    setError(null);

    const description = isRefine ? wizardState.businessDescription : formatDescription();

    // Save description in state
    if (!isRefine) {
      setWizardState((prev) => ({
        ...prev,
        businessDescription: description,
      }));
    }

    try {
      const response = await AgentsFlowService.wizardIdealize({
        business_type: businessType,
        business_description: description,
        iteration: isRefine ? iteration + 1 : 0,
        previous_summary: isRefine ? idealizationSummary : null,
        refinement_notes: isRefine ? refinementNotes : '',
        gender: assistantGender,
      });

      setWizardState((prev) => ({
        ...prev,
        idealizationSummary: response.data,
        _idealizationIteration: isRefine ? iteration + 1 : 0,
      }));
      setShowRefinement(false);
      setRefinementNotes('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao analisar. Tente novamente.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApprove = () => {
    setWizardState((prev) => ({
      ...prev,
      _idealizationApproved: true,
    }));
  };

  const handleGenderChange = (gender) => {
    setWizardState((prev) => ({
      ...prev,
      assistantGender: gender,
      idealizationSummary: null,
      _idealizationApproved: false,
    }));
  };

  const handleBusinessTypeChange = (type) => {
    setWizardState((prev) => ({
      ...prev,
      businessType: type,
      idealizationSummary: null,
      _idealizationApproved: false,
    }));
    // Reset questions when type changes
    setSelectedQuestions({});
    setAnswers({});
    setAdditionalInfo('');
  };

  return (
    <div>
      <h3 className="awc-body__title">Conte-nos sobre seu negócio</h3>
      <p className="awc-body__subtitle">
        Selecione o tipo e responda as perguntas. A IA vai analisar e preparar tudo para você.
      </p>

      <BusinessTypeSelector
        value={businessType}
        onChange={handleBusinessTypeChange}
      />

      {businessType && (
        <div className="awc-gender">
          <p className="awc-gender__label">Como o assistente deve se apresentar?</p>
          <div className="awc-gender__grid" role="radiogroup" aria-label="Gênero do assistente">
            <div
              className={`awc-gender__option${assistantGender === 'female' ? ' awc-gender__option--selected' : ''}`}
              onClick={() => handleGenderChange('female')}
              role="radio"
              aria-checked={assistantGender === 'female'}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGenderChange('female'); } }}
            >
              <IconGenderFemale size={20} className="awc-gender__icon" />
              <span className="awc-gender__name">Feminino</span>
            </div>
            <div
              className={`awc-gender__option${assistantGender === 'male' ? ' awc-gender__option--selected' : ''}`}
              onClick={() => handleGenderChange('male')}
              role="radio"
              aria-checked={assistantGender === 'male'}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGenderChange('male'); } }}
            >
              <IconGenderMale size={20} className="awc-gender__icon" />
              <span className="awc-gender__name">Masculino</span>
            </div>
          </div>
        </div>
      )}

      {businessType && questions.length > 0 && !idealizationSummary && (
        <>
          {/* Banner */}
          <div className="awc-banner">
            <IconInfoCircle size={24} className="awc-banner__icon" />
            <p className="awc-banner__text">
              Quanto mais perguntas responder, mais preciso será seu agente.
              <br />
              <span
                className="awc-banner__detail"
                style={{ color: isOverBudget ? '#d63939' : undefined, fontWeight: isOverBudget ? 600 : undefined }}
              >
                Total: {totalChars.toLocaleString('pt-BR')} / {TOTAL_CHAR_BUDGET.toLocaleString('pt-BR')} caracteres
                {isOverBudget && ` — você ultrapassou em ${(totalChars - TOTAL_CHAR_BUDGET).toLocaleString('pt-BR')} caracteres. Encurte alguma resposta ou as informações adicionais.`}
              </span>
            </p>
          </div>

          {/* Progress bar */}
          {selectedCount > 0 && (
            <>
              <div className="awc-progressbar__label">
                {answeredCount} de {selectedCount} respondidas ({progressPercent}%)
              </div>
              <div className="awc-progressbar">
                <div
                  className="awc-progressbar__fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </>
          )}

          {/* Questions list */}
          <div className="awc-questions">
            {questions.map((q) => {
              const isSelected = !!selectedQuestions[q.id];
              return (
                <div
                  key={q.id}
                  className={`awc-questions__item${isSelected ? ' awc-questions__item--selected' : ''}`}
                >
                  <div
                    className="awc-questions__header"
                    onClick={() => handleToggleQuestion(q.id)}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggleQuestion(q.id); } }}
                  >
                    <div className={`awc-questions__checkbox${isSelected ? ' awc-questions__checkbox--checked' : ''}`}>
                      {isSelected && <IconCheck size={14} />}
                    </div>
                    <span className="awc-questions__text">{q.question}</span>
                  </div>
                  {isSelected && (
                    <div className="awc-questions__answer">
                      <textarea
                        rows={3}
                        placeholder={q.placeholder}
                        value={answers[q.id] || ''}
                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                        onPaste={makePasteHandler(answers[q.id] || '', (next) => handleAnswerChange(q.id, next))}
                        autoFocus
                      />
                      <span className="awc-questions__charcount">
                        {(answers[q.id] || '').length.toLocaleString('pt-BR')} caracteres
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Additional info */}
          <div className="awc-additional-info">
            <label>Informações adicionais (opcional)</label>
            <textarea
              className="awc-textarea awc-textarea--sm"
              placeholder="Qualquer informação extra sobre o seu negócio que possa ajudar..."
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(sanitizePastedText(e.target.value))}
              onPaste={makePasteHandler(additionalInfo, setAdditionalInfo)}
            />
            <span className="awc-questions__charcount">
              {additionalInfo.length.toLocaleString('pt-BR')} caracteres
            </span>
          </div>

          {/* Analyze button */}
          <div className="mt-3 text-end">
            <button
              className="awc-btn awc-btn--primary"
              onClick={() => handleAnalyze(false)}
              disabled={isAnalyzing || !canAnalyze}
            >
              {isAnalyzing ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Analisando...
                </>
              ) : (
                <>
                  <IconSparkles size={16} />
                  Gerar Análise{' '}
                  {isOverBudget
                    ? `(reduza ${(totalChars - TOTAL_CHAR_BUDGET).toLocaleString('pt-BR')} caracteres)`
                    : answeredCount < 3
                      ? `(responda ${3 - answeredCount}+ perguntas)`
                      : ''}
                </>
              )}
            </button>
            <div
              className="mt-1"
              style={{
                fontSize: '0.8125rem',
                color: isOverBudget ? '#d63939' : '#6c757d',
                fontWeight: isOverBudget ? 600 : 400,
              }}
            >
              {totalChars.toLocaleString('pt-BR')} de {TOTAL_CHAR_BUDGET.toLocaleString('pt-BR')} caracteres utilizados
            </div>
          </div>
        </>
      )}

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {idealizationSummary && (
        <div className="mt-4">
          <h4 className="awc-section-heading">
            Análise do seu negócio
          </h4>

          <div className="awc-summary">
            <h4 className="awc-summary__title">{idealizationSummary.business_name || 'Seu negócio'}</h4>
            <p className="awc-summary__text">{idealizationSummary.summary}</p>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="awc-summary">
                <h4 className="awc-summary__title">Serviços identificados</h4>
                <ul className="awc-summary__list">
                  {(idealizationSummary.services || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-md-6">
              <div className="awc-summary">
                <h4 className="awc-summary__title">Objetivos do agente</h4>
                <ul className="awc-summary__list">
                  {(idealizationSummary.agent_goals || []).map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="awc-summary">
                <h4 className="awc-summary__title">Nome sugerido</h4>
                <p className="awc-summary__text">{idealizationSummary.suggested_name}</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="awc-summary">
                <h4 className="awc-summary__title">Função</h4>
                <p className="awc-summary__text">{idealizationSummary.suggested_role}</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="awc-summary">
                <h4 className="awc-summary__title">Tom de voz</h4>
                <p className="awc-summary__text awc-text--capitalize">{idealizationSummary.tone_recommendation}</p>
              </div>
            </div>
          </div>

          {!wizardState._idealizationApproved && (
            <div className="d-flex gap-2 justify-content-end mt-3">
              {iteration < 2 && (
                <button
                  className="awc-btn awc-btn--secondary"
                  onClick={() => setShowRefinement(!showRefinement)}
                >
                  <IconRefresh size={16} />
                  Refinar
                </button>
              )}
              <button
                className="awc-btn awc-btn--success"
                onClick={handleApprove}
              >
                Aprovar e continuar
              </button>
            </div>
          )}

          {wizardState._idealizationApproved && (
            <div className="alert alert-success mt-3 d-flex align-items-center" role="alert">
              <span>Análise aprovada. Avance para o próximo passo.</span>
            </div>
          )}

          {showRefinement && (
            <div className="awc-refinement mt-3">
              <label className="awc-refinement__label">
                O que gostaria de ajustar?
              </label>
              <textarea
                className="awc-textarea awc-textarea--sm"
                placeholder="Ex: O horário é das 8h às 18h, também atendemos emergências..."
                value={refinementNotes}
                onChange={(e) => setRefinementNotes(e.target.value)}
              />
              <div className="mt-2 text-end">
                <button
                  className="awc-btn awc-btn--primary awc-btn--sm"
                  onClick={() => handleAnalyze(true)}
                  disabled={isAnalyzing || !refinementNotes.trim()}
                >
                  {isAnalyzing ? 'Refinando...' : 'Aplicar refinamento'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IdealizationStep;
